import { useCallback, useMemo } from 'react';
import { useEffectReducer } from 'use-effect-reducer';
import { NO_DURATION_DEFINED } from '../../../../helpers/axiosWrapper/cache';
import { getCorrectCurrencySettings } from '../../../../helpers/utilsPricing';
import { useTrackingChange } from '../../../../_integrations/everymundo/tracking/index';
import { useTravelerInterest } from '../../_commons/travelerInterest';
import { getTravelerInterest } from '../../../../_integrations/everymundo/travelerInterest';
import { filtersReducer, FiltersState } from '../../_commons/filtersReducer';
import useBudget from './useBudget';
import useDateRange, { getDateRange, validateDateRange } from './useDateRange';
import useFilterDisplay from './useFilterDisplay';
import useFlightType from './useFlightType';
import { Airport } from './useLocations';
import useOriginDestination from './useOriginDestination';
import useSorting, { transformSortingOption } from './useSorting';
import useTravelClass from './useTravelClass';
import useFlightStopCount from '../../_commons/flightStopCount';

export const OriginDestinationLogic = {
  SAME: 'same',
  DIFFERENT: 'different'
};

interface UseFiltersProps {
  availableFilters?: string[];
  availableSecondaryFilters?: string[];
  hideBudgetOnMultiCurrency?: boolean;
  filter: any;
  filterSettings?: any;
  initialOrigins?: Airport[];
  initialDestinations?: Airport[];
  originDestinationLogic?: string;
  cache?: boolean;
  cacheDuration?: number;
  showInitialData?: boolean;
}

const useFilters = ({
  availableFilters = [],
  availableSecondaryFilters = [],
  hideBudgetOnMultiCurrency = true,
  filter,
  filter: { prePopulation, maxItemsAmount, integrationSettings, meta, pageContext },
  initialOrigins,
  initialDestinations,
  originDestinationLogic = OriginDestinationLogic.DIFFERENT,
  cache = false,
  cacheDuration = NO_DURATION_DEFINED,
  showInitialData = false,
  filterSettings: { dateRangeSettings }
}: UseFiltersProps) => {
  const initialState: FiltersState = useMemo(
    () => ({
      originDestinationLogic,
      origin: { value: prePopulation.origin || prePopulation.originCity || '' },
      destination: { value: prePopulation.destination || prePopulation.destinationCity || '' },
      dateRange: validateDateRange({
        departure: dateRangeSettings.departure,
        dateRange: getDateRange(prePopulation, maxItemsAmount)
      }),
      budget: (prePopulation.budget && prePopulation.budget.maximum) || null,
      travelClass: prePopulation.travelClasses,
      sorting: transformSortingOption(prePopulation.sorting),
      flightType: prePopulation.flightType,
      flightStopCount: prePopulation.flightStopCount,
      formPrepopuled: true,
      travelerInterest: {},
      navigation: 'all',
      isFullMode: false
    }),
    [dateRangeSettings.departure, maxItemsAmount, originDestinationLogic, prePopulation]
  );

  const {
    outputCurrencies = [],
    priceFormat = {},
    currencySettings = [],
    currencyPricingSettings
  } = prePopulation;
  const displayedFilters = useFilterDisplay({
    outputCurrencies,
    availableFilters,
    hideBudgetOnMultiCurrency
  });

  const displayedSecondaryFilters = useFilterDisplay({
    outputCurrencies,
    availableFilters: availableSecondaryFilters,
    hideBudgetOnMultiCurrency
  });

  const track = useTrackingChange();
  const tracking = useCallback(
    (_, event) => {
      track(event.prevState, event.action, _);
    },
    [track]
  );

  const [state, dispatch] = useEffectReducer(filtersReducer, initialState, { tracking });

  const travelerInterest = useTravelerInterest({
    dispatch,
    getTravelerInterest,
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    selectedItems: state.travelerInterest
  });

  const locations = useOriginDestination({
    dispatch,
    origin: state.origin as Airport,
    destination: state.destination as Airport,
    filter,
    initialOrigins,
    initialDestinations,
    originDestinationLogic,
    cache,
    cacheDuration,
    travelerInterest: travelerInterest?.selectedItems,
    showInitialData
  });

  const { currencyCode, currencyToDisplay } = useMemo(
    () =>
      getCorrectCurrencySettings(
        priceFormat,
        currencyPricingSettings || currencySettings,
        outputCurrencies[0]
      ),
    [currencyPricingSettings, currencySettings, outputCurrencies, priceFormat]
  );

  const dateRange = useDateRange({ dispatch, value: state.dateRange });
  const budget = useBudget({
    value: state.budget,
    dispatch,
    currencyCode: currencyToDisplay || currencyCode
  });
  const travelClass = useTravelClass({
    dispatch,
    value: state.travelClass,
    initValue: prePopulation.travelClasses
  });
  const sorting = useSorting({ dispatch, value: state.sorting });
  const flightType = useFlightType({
    dispatch,
    value: state.flightType,
    initValue: prePopulation.flightType
  });
  const flightStopCount = useFlightStopCount({
    dispatch,
    value: state.flightStopCount,
    initValue: prePopulation.flightStopCount
  });
  const onClear = useCallback(() => {
    let newPayload: any = {
      travelClass: prePopulation.travelClasses,
      sorting: transformSortingOption(prePopulation.sorting),
      flightType: prePopulation.flightType,
      flightStopCount: prePopulation.flightStopCount,
      formPrepopuled: true,
      travelerInterest: {}
    };
    if (dateRange.defaultValue.checkedFlexibleDates !== undefined) {
      newPayload['dateRange'] = {
        ...dateRange.defaultValue
      };
    }

    dispatch({
      type: 'CLEAR_ALL',
      payload: newPayload
    });
    dateRange.onClear();
    dateRange.clear();
    budget.clear();
  }, [
    budget,
    dateRange,
    dispatch,
    prePopulation.flightType,
    prePopulation.flightStopCount,
    prePopulation.sorting,
    prePopulation.travelClasses
  ]);

  const onClearFiltersSecondary = useCallback(
    (payload) => {
      let newPayload: any = {
        travelClass: prePopulation.travelClasses,
        sorting: transformSortingOption(prePopulation.sorting),
        flightType: prePopulation.flightType,
        flightStopCount: prePopulation.flightStopCount,
        formPrepopuled: true,
        ...payload
      };

      //QUESTION:  What is this used for?

      // if (dateRange.defaultValue.checkedFlexibleDates !== undefined) {
      //   newPayload['dateRange'] = {
      //     ...dateRange.defaultValue
      //   };
      // }

      dispatch({
        type: 'CLEAR_PARTIAL',
        payload: newPayload
      });

      //TODO: There could be a problem when the dateRange or the
      //      budget is enabled in the primary filters and the clearAll
      //      of the secondary filters is clicked.
      dateRange.clear();
      budget.clear();
    },
    [
      budget,
      dateRange,
      dispatch,
      prePopulation.flightType,
      prePopulation.flightStopCount,
      prePopulation.sorting,
      prePopulation.travelClasses
    ]
  );

  const isError = locations.origin.isError || locations.destination.isError;

  const refetch = useMemo(
    () => () => {
      if (locations.origin.isError) {
        locations.origin.refetch();
      }
      if (locations.destination.isError) {
        locations.destination.refetch();
      }
    },
    [locations]
  );

  const trackingDispatch = useCallback(
    (event) => {
      return dispatch({ ...event, category: 'tracking ' });
    },
    [dispatch]
  );

  return {
    isError,
    refetch,
    locations,
    dateRange,
    budget,
    travelClass,
    flightType,
    flightStopCount,
    travelerInterest,
    sorting,
    dispatch,
    onClear,
    onClearFiltersSecondary,
    state,
    availableFilters: displayedFilters,
    availableSecondaryFilters: displayedSecondaryFilters,
    tracking: trackingDispatch,
    navigation: state?.navigation,
    isFullMode: state?.isFullMode
  };
};

export default useFilters;
